import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import ExcelImport from '../components/settings/ExcelImport';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Building2, Mail, Phone, MapPin, Bell, Settings, Upload, Cog, Shield, Database } from 'lucide-react';
import ColorSchemeSettings from '../components/settings/ColorSchemeSettings';
import QuickBooksSettings from '../components/settings/QuickBooksSettings';

const defaultSettings = {
  company_name: '',
  company_email: '',
  company_phone: '',
  company_address: '',
  low_stock_alerts: true,
  transaction_notifications: true,
  email_notifications: true
};

const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [user]);

  const fetchSettings = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data);
      } else {
        // If no settings exist, use default values
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // On error, keep using default settings
      setSettings(defaultSettings);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (!user) return;

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          company_name: settings.company_name,
          company_email: settings.company_email,
          company_phone: settings.company_phone,
          company_address: settings.company_address,
          low_stock_alerts: settings.low_stock_alerts,
          transaction_notifications: settings.transaction_notifications,
          email_notifications: settings.email_notifications
        }, { 
          onConflict: 'user_id' 
        });

      if (error) throw error;

      setMessage('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
      setMessage('Failed to update settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/442150/pexels-photo-442150.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Computer system configuration with circuit boards and technical settings"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Settings</h1>
              <p className="text-lg opacity-90">
                Configure your system preferences and company information
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Cog className="h-4 w-4 mr-2" />
                  <span>System Configuration</span>
                </div>
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-2" />
                  <span>Security Settings</span>
                </div>
                <div className="flex items-center">
                  <Database className="h-4 w-4 mr-2" />
                  <span>Data Management</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid gap-6">
        <Card>
          <form onSubmit={handleSubmit} className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <Building2 className="h-5 w-5" />
              Company Information
            </h2>
            
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">Company Name</label>
                <Input
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">Company Email</label>
                <div className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-gray-600" />
                  <Input
                    name="company_email"
                    type="email"
                    value={settings.company_email}
                    onChange={handleChange}
                    placeholder="Enter company email"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">Company Phone</label>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 mr-2 text-gray-600" />
                  <Input
                    name="company_phone"
                    value={settings.company_phone}
                    onChange={handleChange}
                    placeholder="Enter company phone"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-800 dark:text-white">Company Address</label>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-gray-600" />
                  <Input
                    name="company_address"
                    value={settings.company_address}
                    onChange={handleChange}
                    placeholder="Enter company address"
                  />
                </div>
              </div>
            </div>
          </form>
        </Card>

        <Card>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="low_stock_alerts"
                  checked={settings.low_stock_alerts}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-800 dark:text-white">
                  Low stock alerts
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="transaction_notifications"
                  checked={settings.transaction_notifications}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-800 dark:text-white">
                  Transaction notifications
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="email_notifications"
                  checked={settings.email_notifications}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-800 dark:text-white">
                  Email notifications
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Excel Import Section */}
        <ExcelImport />

        {/* Color Scheme Selection */}
        <ColorSchemeSettings />

        {/* QuickBooks Integration */}
        <QuickBooksSettings />

        <div className="flex justify-end gap-4">
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {message && (
          <p className={`mt-4 text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;